/**
 * Costline Premium: what it unlocks and where the answer comes from.
 *
 * Free forever: adding by hand, every budget on this device, analytics,
 * bills, export. Premium: a shared (household) budget, cloud sync of your
 * own budgets, Smart Bill Import, and no ads. A shared budget is covered by
 * its owner's Premium: the people invited to it do not pay.
 */
export const PREMIUM_FEATURES = ['household', 'sync', 'billImport', 'noAds'] as const;
export type PremiumFeature = (typeof PREMIUM_FEATURES)[number];

/** The RevenueCat entitlement every Premium product (monthly, yearly) grants. */
export const ENTITLEMENT_ID = 'premium';

export interface Entitlement {
  active: boolean;
  /** When it ends unless renewed (ms); null for none, or no end date known. */
  expiresAt: number | null;
  /** Who said so: the store on this device, or the account (bought on another device). */
  source: 'store' | 'account' | null;
  /** Renews at expiresAt (false: cancelled, runs out then); null when unknown. */
  willRenew?: boolean | null;
}

export const NO_ENTITLEMENT: Entitlement = { active: false, expiresAt: null, source: null };

/**
 * Where a device learns whether this person has Premium. A check never
 * throws: anything that goes wrong reads as "not Premium" (fail closed), and
 * nothing is lost by that, since everything Premium waits rather than
 * disappears (see sync's hold).
 */
export interface EntitlementSource {
  check(userId: string): Promise<Entitlement>;
  /** The store's own news (a purchase, a renewal, an expiry); returns an unsubscribe. */
  subscribe?(onChange: (e: Entitlement) => void): () => void;
}

/** A source with a fixed answer: builds without a store (the web until accounts carry Premium), and tests. */
export function fixedEntitlement(active: boolean): EntitlementSource {
  const e: Entitlement = active ? { active: true, expiresAt: null, source: 'store' } : NO_ENTITLEMENT;
  return { check: async () => e };
}

/** The two ways to pay (the RevenueCat offering's monthly and annual packages). */
export type PlanId = 'monthly' | 'yearly';

export interface PremiumPlan {
  id: PlanId;
  /** The store's own price, in the buyer's currency: "$1.99", "₺89,99". Never a number of ours. */
  price: string;
  /** For comparing plans (the store's number, in the same currency). */
  amount: number;
  currency: string;
  /** Yearly: what a month comes to ("$1.67"); null for monthly or when the store doesn't say. */
  perMonth: string | null;
}

export type PurchaseOutcome =
  | { ok: true; entitlement: Entitlement }
  /** cancelled: the person closed the store sheet; pending: waiting for approval (Ask to Buy). */
  | { ok: false; reason: 'cancelled' | 'pending' | 'failed' };

/** Buying Premium: only on a phone (App Store, Google Play). The web has none: Premium reaches it through the account. */
export interface Storefront {
  /** The plans on sale; [] when the store cannot be reached. */
  plans(): Promise<PremiumPlan[]>;
  buy(plan: PlanId): Promise<PurchaseOutcome>;
  /** Purchases made with this Apple ID / Google account, on this or another device. */
  restore(): Promise<Entitlement>;
  /** The store's own page for cancelling or changing the plan. */
  manageUrl(): Promise<string | null>;
}
